import React, { useEffect, useState, useRef } from 'react';
import EmigoInteractiveIcon from './EmigoInteractiveIcon';
const features = [{
  id: 1,
  name: 'Reframe',
  subheading: "Don't let one text ruin everything.",
  description: '',
  bullets: ["Stop sending the paragraph you'll regret in 10 minutes", 'Say what hurt you without starting another fight', "Don't let one emotional text make everything worse"],
  previewImage: 'https://cdn.magicpatterns.com/uploads/oVDHy98J8wBcZeLM3iWL3o/Reframe.jpg'
}, {
  id: 2,
  name: 'Interpret',
  subheading: 'Read the message without spiraling.',
  description: '',
  bullets: ['Stop rereading the same text over and over', "Stop always assuming there's something wrong", "Don't let one message ruin your whole day"],
  previewImage: 'https://cdn.magicpatterns.com/uploads/mocQZmQNiwZUzeuqp57XTD/Interpret.jpg'
}, {
  id: 3,
  name: 'Create',
  subheading: 'Make your words match the moment.',
  description: '',
  bullets: ['Stop staring at the screen not knowing what to say', 'Say what you mean without overthinking every word', 'Write captions, posts, and texts that actually land'],
  previewImage: 'https://cdn.magicpatterns.com/uploads/svhJqVPZKw11DsQqHtQpss/Create_(1).jpg'
}, {
  id: 4,
  name: 'Unpack',
  subheading: 'When one conversation keeps living in your head.',
  description: '',
  bullets: ['Stop replaying every word over and over', 'Understand what they meant, not just how it felt', 'Figure out what actually happened before it eats at you'],
  previewImage: 'https://cdn.magicpatterns.com/uploads/mewsxj74VLcQnNSRbp7D2A/Unpack.jpg'
}, {
  id: 5,
  name: 'Coming Soon',
  subheading: 'More powerful tools are on the way.',
  description: 'We are constantly building new ways to help you communicate better and build stronger relationships.',
  bullets: ['Pause — take a moment before you send', 'Analytics — understand your communication patterns'],
  isComingSoon: true
}];
const FeaturesSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [hasBeenTapped, setHasBeenTapped] = useState(false);
  const sectionRef = useRef(null);
  const totalSlides = features.length;
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const pauseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Intersection observer for animation on scroll
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting) {
        setIsVisible(true);
      }
    };
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);
  // Auto-cycle logic — only cycle through first 4 features (exclude Coming Soon)
  useEffect(() => {
    if (isPaused) return;
    timerRef.current = setInterval(() => {
      setCurrentSlide(prev => prev >= 3 ? 0 : prev + 1);
    }, 6000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPaused, totalSlides]);
  const handleFeatureClick = (index: number) => {
    setCurrentSlide(index);
    setIsPaused(true);
    // Clear existing pause timeout if any
    if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
    // Resume after 15 seconds
    pauseTimeoutRef.current = setTimeout(() => {
      setIsPaused(false);
    }, 15000);
  };
  // Cleanup pause timeout
  useEffect(() => {
    return () => {
      if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
    };
  }, []);
  return <section id="features" ref={sectionRef} className="relative pt-10 pb-16 overflow-hidden bg-white lg:min-h-screen flex items-start lg:pt-20" data-id="element-60">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-61">
        {/* UNIFIED HERO HEADER */}
        <div className={`text-left mb-1 lg:mb-28 transition-all duration-1000 ease-out transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-62">
          {/* Main Headline */}
          <h1 className="text-4xl md:text-5xl lg:text-7xl font-semibold text-gray-900 mb-0 lg:mb-4 flex flex-wrap items-center gap-x-3 gap-y-0 justify-start leading-none" style={{
          fontFamily: "'DM Serif Display', serif"
        }} data-id="element-63">
            <span className="inline-flex items-center gap-3" data-id="element-64">
              <EmigoInteractiveIcon onTap={() => setHasBeenTapped(true)} data-id="element-65" />
              <span className="text-[#E87B5C]" data-id="element-66">One Tap</span>
            </span>
            <span className={`w-full lg:w-auto pl-[9.5rem] md:pl-0 -mt-3 lg:mt-0 text-lg md:text-2xl lg:text-5xl italic font-normal text-gray-400 lg:text-gray-900 lg:not-italic lg:font-semibold transition-all duration-700 ease-out ${hasBeenTapped ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4 pointer-events-none'}`} data-id="element-67">
              is all it takes to...
            </span>
          </h1>
        </div>

        {/* FEATURE SHOWCASE (INTERACTIVE) */}
        <div className={`flex flex-col lg:flex-row gap-2 lg:gap-12 transition-all duration-1000 delay-300 ease-out transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'}`} data-id="element-68">
          {/* LEFT COLUMN: Content, Tabs, and CTAs */}
          <div className="w-full lg:w-1/2 flex flex-col" data-id="element-69">
            {/* Active Feature Content */}
            <div className="relative min-h-0 lg:min-h-[280px] mb-8" data-id="element-70">
              {features.map((feature, index) => <div key={feature.id} className={`absolute inset-0 transition-all duration-500 ease-in-out ${currentSlide === index ? 'opacity-100 translate-y-0 pointer-events-auto relative' : 'opacity-0 translate-y-4 pointer-events-none absolute'}`} data-id="element-71">
                  <h3 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-900 mb-3" style={{
                fontFamily: "'DM Serif Display', serif"
              }} data-id="element-72">
                    {feature.name}
                  </h3>
                  <p className="text-lg md:text-xl font-semibold text-[#E87B5C] mb-4" data-id="element-73">
                    {feature.subheading}
                  </p>
                  {feature.description && <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6" data-id="element-74">
                      {feature.description}
                    </p>}

                  <ul className="space-y-3" data-id="element-75">
                    {feature.bullets.map((bullet, i) => <li key={i} data-id="element-76">
                        <span className="text-gray-700 text-base md:text-lg" data-id="element-77">
                          {bullet}
                        </span>
                      </li>)}
                  </ul>
                </div>)}
            </div>

            {/* Horizontal Feature Tabs */}
            <div className="flex items-center gap-1 mb-10 overflow-x-auto scrollbar-hide" data-id="element-78">
              {features.map((feature, index) => <button key={feature.id} onClick={() => handleFeatureClick(index)} className={`relative px-3 py-2 text-xs font-medium transition-all duration-300 whitespace-nowrap ${currentSlide === index ? 'text-gray-800' : 'text-gray-300 hover:text-gray-500'}`} data-id="element-79">
                  <span className="flex items-center gap-1" data-id="element-80">
                    {feature.name}
                  </span>
                  {currentSlide === index && <div className="absolute bottom-0 left-3 right-3 h-[1.5px] bg-[#E87B5C]/70 rounded-full" data-id="element-81"></div>}
                </button>)}
            </div>

            {/* CTAs and Social */}
            <div className="flex flex-col items-start gap-3 mt-auto" data-id="element-82">
              {/* Orange CTA Shelf */}
              <div className="relative w-full max-w-[480px]" data-id="element-83">
                <div className="bg-[#E87B5C] rounded-xl px-5 py-3 shadow-md" data-id="element-84">
                  <div className="flex items-center gap-4" data-id="element-85">
                    <a href="https://apps.apple.com/us/app/emigo-better-texting/id6748092950" target="_blank" rel="noopener noreferrer" className="inline-block hover:opacity-90 transition-opacity flex-shrink-0" data-id="element-86">
                      <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/55BSmF1FDX8kQyzwZAZdd8/download-on-the-app-store.svg" alt="Download on the App Store" className="h-9" data-id="element-87" />
                    </a>
                    <p className="text-white/85 text-xs leading-snug" data-id="element-88">
                      Download Emigo for free. No sign-up or personal info
                      required. Just install and start typing.
                    </p>
                  </div>
                </div>

                {/* Social icons + trademark */}
                <div className="flex items-center gap-4 mt-4 pl-1" data-id="element-89">
                  <div className="flex items-center gap-2.5" data-id="element-90">
                    {/* Instagram */}
                    <a href="https://www.instagram.com/emigo.us/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-[#E87B5C] transition-colors" aria-label="Instagram" data-id="element-91">
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-92">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" data-id="element-93" />
                      </svg>
                    </a>
                    {/* Facebook */}
                    <a href="#" className="text-gray-300 hover:text-[#E87B5C] transition-colors" aria-label="Facebook" data-id="element-94">
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-95">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" data-id="element-96" />
                      </svg>
                    </a>
                    {/* Reddit */}
                    <a href="#" className="text-gray-300 hover:text-[#E87B5C] transition-colors" aria-label="Reddit" data-id="element-97">
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-98">
                        <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" data-id="element-99" />
                      </svg>
                    </a>
                    {/* TikTok */}
                    <a href="https://www.tiktok.com/@emigo.us" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-[#E87B5C] transition-colors" aria-label="TikTok" data-id="element-100">
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-101">
                        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" data-id="element-102" />
                      </svg>
                    </a>
                  </div>
                  <span className="text-gray-300 text-[10px]" data-id="element-103">
                    &copy; {new Date().getFullYear()} Emigo&trade;
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Phone Mockup */}
          <div className="w-full lg:w-1/2 flex justify-center lg:justify-end items-start lg:-mt-52" data-id="element-104">
            <div className="relative w-full max-w-[300px] md:max-w-[354px]" data-id="element-105">
              {/* Phone Frame */}
              <div className="relative rounded-[40px] border-[8px] border-gray-900 bg-gray-900 shadow-2xl overflow-hidden aspect-[9/19.5]" data-id="element-106">
                {/* Dynamic Content inside phone */}
                <div className="flex transition-transform duration-500 ease-out h-full w-full" style={{
                transform: `translateX(-${currentSlide * 100}%)`
              }} data-id="element-107">
                  {features.map((feature, index) => <div key={feature.id} className="w-full h-full flex-shrink-0 bg-white" data-id="element-108">
                      {feature.isComingSoon ? <div className="w-full h-full bg-gray-100 flex flex-col px-7 pt-14 pb-8" data-id="element-109">
                          <div className="mb-10" data-id="element-110">
                            <h4 className="text-2xl font-semibold text-gray-900 mb-2" style={{
                        fontFamily: "'DM Serif Display', serif"
                      }} data-id="element-111">
                              Future Updates
                            </h4>
                            <p className="text-gray-500 text-sm leading-relaxed" data-id="element-112">
                              More tools to help you communicate better.
                            </p>
                          </div>

                          <div className="space-y-5 flex-grow flex flex-col justify-center" data-id="element-113">
                            <div className="bg-white rounded-xl px-5 py-5 shadow-sm border border-gray-200" data-id="element-114">
                              <h5 className="text-gray-900 text-lg font-semibold mb-2" style={{
                          fontFamily: "'DM Serif Display', serif"
                        }} data-id="element-115">
                                Pause
                              </h5>
                              <p className="text-gray-500 text-xs leading-relaxed" data-id="element-116">
                                Temporarily lock your keyboard when emotions run
                                too high to prevent regrettable messages.
                              </p>
                            </div>

                            <div className="bg-white rounded-xl px-5 py-5 shadow-sm border border-gray-200" data-id="element-117">
                              <h5 className="text-gray-900 text-lg font-semibold mb-2" style={{
                          fontFamily: "'DM Serif Display', serif"
                        }} data-id="element-118">
                                Analytics
                              </h5>
                              <p className="text-gray-500 text-xs leading-relaxed" data-id="element-119">
                                Track emotional trends over time to understand
                                patterns and triggers.
                              </p>
                            </div>
                          </div>
                        </div> : <img src={feature.previewImage} alt={`${feature.name} preview`} className="w-full h-full object-cover" data-id="element-120" />}
                    </div>)}
                </div>
              </div>

              {/* Decorative shadow under phone */}
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-3/4 h-6 bg-black/20 blur-xl rounded-full" data-id="element-121"></div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default FeaturesSection;